// src/components/compliance/modals/AllActiveTasksModal.jsx

import React from "react";
import {
  buildControlLabel,
  cleanValue,
  formatTimestamp,
  getSummaryChipStyle,
  getTaskStatusLabel,
  normalizeText,
  safeNumber,
} from "../../../utils/complianceFormatters";
import { isTaskOverdue } from "../../../utils/remediationTaskHelpers";
import ModalShell from "./shared/ModalShell";
import MetricTile from "./shared/MetricTile";
import ModalSection from "./shared/ModalSection";
import TaskQueueCard from "./shared/TaskQueueCard";

const metricGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
  gap: 16,
};

function getTaskTitle(task = {}) {
  const title = cleanValue(task.title || task.task_title, "");
  if (title) return title;

  return `Remediate ${buildControlLabel(task.control_id || task.controlId, task.rule_id || task.ruleId)}`;
}

function getOwnerLabel(task = {}) {
  return cleanValue(task.owner_name || task.ownerName || task.owner_user_id || task.ownerUserId, "Unassigned");
}

function getPriorityLabel(task = {}) {
  const priority = task.priority ?? task.priority_score ?? task.priorityScore;
  return priority === null || priority === undefined || priority === "" ? "P—" : `P${priority}`;
}

function getDueDateValue(task = {}) {
  return task.due_date || task.dueDate || task.deadline_at || task.deadlineAt || null;
}

function getLastUpdatedValue(task = {}) {
  return task.updated_at || task.updatedAt || task.created_at || task.createdAt || task.due_date || task.dueDate || null;
}

function statusTone(task = {}) {
  const status = normalizeText(task.status || task.task_status);
  const validationStatus = normalizeText(task.validation_status || task.validationStatus);

  if (validationStatus === "failed") return "danger";
  if (isTaskOverdue(task)) return "danger";
  if (status === "in_progress") return "warn";
  if (status === "ready_for_validation") return "accent";
  if (status === "blocked") return "warn";
  return "default";
}

function validationLabel(task = {}) {
  const raw = cleanValue(task.validation_status || task.validationStatus, "pending");
  return raw.replace(/_/g, " ");
}

function buildDriftParams(task = {}, framework) {
  return {
    framework: task.framework || framework,
    controlId: task.control_id || task.controlId || undefined,
    ruleId: task.rule_id || task.ruleId || undefined,
    resourceId: task.resource_id || task.resourceId || undefined,
  };
}

function getTaskKey(task = {}, index) {
  return (
    task.id ||
    [
      task.framework,
      task.control_id || task.controlId,
      task.rule_id || task.ruleId,
      task.resource_id || task.resourceId,
      index,
    ]
      .filter(Boolean)
      .join("::")
  );
}

function sortActiveTasks(tasks = []) {
  return [...(Array.isArray(tasks) ? tasks : [])].sort((left, right) => {
    const rightOverdue = isTaskOverdue(right) ? 1 : 0;
    const leftOverdue = isTaskOverdue(left) ? 1 : 0;
    if (rightOverdue !== leftOverdue) return rightOverdue - leftOverdue;

    const rightUpdated = new Date(getLastUpdatedValue(right) || 0).getTime();
    const leftUpdated = new Date(getLastUpdatedValue(left) || 0).getTime();
    const rightTime = Number.isFinite(rightUpdated) ? rightUpdated : 0;
    const leftTime = Number.isFinite(leftUpdated) ? leftUpdated : 0;
    if (rightTime !== leftTime) return rightTime - leftTime;

    return safeNumber(left.priority, 999) - safeNumber(right.priority, 999);
  });
}

function buildTaskActions({ task, framework, onReviewTask, onFixPlan, onViewGuidance, onOpenDrift }) {
  return [
    {
      key: "review",
      label: "Review / Manage",
      onClick: () => onReviewTask(task, { returnTo: "all_active_tasks" }),
    },
    {
      key: "fix_plan",
      label: "Fix Plan",
      onClick: () => onFixPlan(task, "task", { returnTo: "all_active_tasks" }),
    },
    {
      key: "guidance",
      label: "Guidance",
      onClick: () => onViewGuidance(task, { returnTo: "all_active_tasks" }),
    },
    {
      key: "drift",
      label: "Open in Drift",
      onClick: () => onOpenDrift(buildDriftParams(task, framework)),
    },
  ];
}

export default function AllActiveTasksModal({
  open,
  framework,
  activeTasks = [],
  totalCount = 0,
  loading = false,
  error = "",
  onClose,
  onReviewTask,
  onFixPlan,
  onViewGuidance,
  onOpenDrift,
}) {
  if (!open) return null;

  const rows = sortActiveTasks(activeTasks);
  const shownCount = rows.length;
  const total = totalCount || shownCount;
  const inProgressCount = rows.filter((task) => normalizeText(task.status) === "in_progress").length;
  const validationFailedCount = rows.filter(
    (task) => normalizeText(task.validation_status || task.validationStatus) === "failed"
  ).length;
  const overdueCount = rows.filter((task) => isTaskOverdue(task)).length;
  const unassignedCount = rows.filter((task) => getOwnerLabel(task) === "Unassigned").length;

  return (
    <ModalShell
      open={open}
      ariaLabel="All active remediation work"
      eyebrow="OASYS · ACTIVE WORK"
      title="All Active Remediation Work"
      subtitle="Review open remediation work across the selected framework without expanding the main operations page."
      onClose={onClose}
    >
      <div style={metricGridStyle}>
        <MetricTile label="Active Tasks" value={total} sub={`${framework} active work`} />
        <MetricTile
          label="In Progress"
          value={inProgressCount}
          sub="being worked"
          color={inProgressCount > 0 ? "#fbbf24" : "#34d399"}
        />
        <MetricTile
          label="Validation Failed"
          value={validationFailedCount}
          sub="needs fix pass"
          color={validationFailedCount > 0 ? "#f87171" : "#34d399"}
        />
        <MetricTile
          label="Overdue"
          value={overdueCount}
          sub="SLA breached"
          color={overdueCount > 0 ? "#f87171" : "#34d399"}
        />
        <MetricTile
          label="Unassigned"
          value={unassignedCount}
          sub="missing owner"
          color={unassignedCount > 0 ? "#fbbf24" : "#34d399"}
        />
      </div>

      {error ? (
        <div style={{ ...getSummaryChipStyle("danger"), justifySelf: "start", borderRadius: 12 }}>
          {error}
        </div>
      ) : null}

      <ModalSection
        title="Active Work Queue"
        description="Use Review / Manage to update task ownership or workflow state, Fix Plan to complete remediation, or Guidance to inspect context."
        badge={loading ? "loading…" : `${shownCount} shown${total > shownCount ? ` of ${total}` : ""}`}
        badgeTone="ok"
      >
        {loading ? (
          <div className="muted" style={{ padding: 14 }}>
            Loading active remediation work…
          </div>
        ) : rows.length === 0 ? (
          <div className="muted" style={{ padding: 14 }}>
            No active remediation tasks are currently loaded for {framework}.
          </div>
        ) : (
          rows.map((task, index) => {
            const controlId = task.control_id || task.controlId || "";
            const ruleId = task.rule_id || task.ruleId || "";
            const resourceId = task.resource_id || task.resourceId || "";
            const dueDate = getDueDateValue(task);
            const ownerLabel = getOwnerLabel(task);
            const taskStatus = getTaskStatusLabel(task.status || task.task_status || "open");
            const tone = statusTone(task);
            const isValidationFailed = normalizeText(task.validation_status) === "failed";

            return (
              <TaskQueueCard
                key={getTaskKey(task, index)}
                title={getTaskTitle(task)}
                dueLabel={dueDate ? `Due ${formatTimestamp(dueDate)}` : "No due date"}
                chips={[
                  { label: taskStatus, tone },
                  { label: validationLabel(task), tone: isValidationFailed ? "danger" : "default" },
                  { label: ownerLabel, tone: ownerLabel === "Unassigned" ? "warn" : "default" },
                  { label: getPriorityLabel(task), tone: "default" },
                ]}
                meta={[
                  { label: "Control", value: cleanValue(controlId, "—") },
                  { label: "Rule", value: cleanValue(ruleId, "—") },
                  { label: "Resource", value: cleanValue(resourceId, "—"), wrap: true },
                ]}
                context={
                  task.failure_reason || task.reason_summary || task.remediation_hint
                    ? cleanValue(task.failure_reason || task.reason_summary || task.remediation_hint, "Review the task for remediation details.")
                    : ""
                }
                actions={buildTaskActions({ task, framework, onReviewTask, onFixPlan, onViewGuidance, onOpenDrift })}
              />
            );
          })
        )}
      </ModalSection>
    </ModalShell>
  );
}
